<template>
  <div class="group relative f-col" data-test-id="product-card">
    <base-button
      v-if="!isComingSoon && !isRobisOnly"
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-3 p-1 group-@hover:op-100 duration lg:op-0 group-focus-within:lg:op-100"
      data-test-id="product-card-add"
      style="margin-right: 0.125rem"
      @click="openQuickshop"
    >
      <span class="m-1 block rounded-xs bg-white/75 duration">
        <vf-icon name="quickshop" size="md" />
      </span>
    </base-button>
    <product-card-gallery
      :fetchpriority
      :items="data.gallery"
      :lazy
      :name="data.name"
      :show-secondary-media="$feature.enableProductCardGalleryCarousel"
    >
      <template #badge>
        <product-badge
          v-if="hasBadge"
          v-bind="data.badge"
          class="backdrop-blur-xs absolute rounded-xs bg-white/50 px-1 py-.5 shadow-sm"
          data-test-id="product-card-badge"
          style="top: 0.5rem; left: 0.75rem"
        />
      </template>
      <template #link>
        <base-link aria-hidden="true" class="absolute-0" tabindex="-1" :to="url" @click.capture="navigateToPDP" />
      </template>
    </product-card-gallery>
    <div
      class="f-col pb-1 pl-3 pr-2 pt-3 lg:p-2"
      :class="{ relative: $feature.enableProductCardGalleryCarousel }"
      style="gap: 0.125rem"
    >
      <div
        v-if="showEyebrow"
        class="line-clamp-1 eyebrow c-brand-1"
        data-test-id="product-card-badge"
        style="height: 1rem"
      >
        {{ data.eyebrow?.label }}
      </div>
      <base-link
        :id="product.id"
        class="subtitle-6 line-clamp-2 self-start"
        data-test-id="product-card-title"
        :to="url"
        @click.capture="navigateToPDP"
      >
        {{ data.name }}
      </base-link>
      <div v-if="$feature.showShortDescriptionInProductList && data.shortDescription" class="body-4 c-grey-30">
        {{ data.shortDescription }}
      </div>
      <span v-if="colorsCount" class="body-5 flex c-brand-1">
        {{ colorsCount > 15 ? '15+' : colorsCount }} {{ $pluralize($t.colors, colorsCount).toLowerCase() }}
      </span>
      <product-pricing
        class="body-5"
        compact-omnibus
        :currency="data.currency"
        data-test-id="product-card-pricing"
        :price="productPrice"
        :show-discount-percentage="showDiscountPercentage"
      />
      <div v-if="$feature.showBuyWithPrimeOnPLP && product?.primeIntended" class="body-5 mt-2 flex items-center">
        <base-picture alt="Prime" class="mr-2" height="24" src="/img/logos/prime.svg" width="45" />{{ $t.optionsAvailable }}
      </div>
    </div>

    <panel-quickshop v-slot="props" class-wrapper="!p-0" :size="{ sm: 'lg', lg: 'full' }" :title="$t.size">
      <dialog-quickshop v-bind="props" :product-id="swatchId" />
    </panel-quickshop>
  </div>
</template>

<script lang="ts" setup>
import type { Product, ProductCardExtended } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { Breadcrumb, ColorOption } from '#types/product'

const {
  breadcrumbs = [],
  imageFetchPriority: fetchpriority,
  trackingOffset,
  product,
  storeFilterState,
  trackingCategory
} = defineProps<{
  /**
   * Page breadcrumbs from the product list
   */
  breadcrumbs?: Breadcrumb[]
  /**
   * Controls lazy load property of the default product image
   */
  lazy?: boolean
  /**
   * Product data
   */
  product: Product | ProductCardExtended
  /**
   * Tracking category for the click and details analytics events
   */
  trackingCategory?: string
  /**
   * Sets displaying the discount amount as percentage
   */
  showDiscountPercentage?: boolean
  /**
   * The id of the selected pickup store in the filters of the parent product list, if any
   */
  storeFilterState?: string
  /**
   * Set fetchpriority for the first product image
   */
  imageFetchPriority?: 'low' | 'high' | 'auto'
  /**
   * The index of the card in the product list, used for GTM tracking
   */
  trackingOffset?: number
}>()

const { $feature, $gtm } = useNuxtApp()
const origin = useOrigin()
const route = useRoute()
const router = useRouter()

const PanelQuickshop = createPanel('quickshop')

/*
 Some products can be marketed by Style (NF0A3XEO) or Style Color (NF0A3XEOLE4).
 When marketed by style we need to find the default color from the color attributes.
*/
const isStyle = PRODUCT_STYLE_ID_REGEX.test(product?.id)
const productUrl = ref(product.url)
const defaultColor = (isStyle ? getDefaultColorOption(product.attributes, product.id) || {} : {}) as ColorOption
const activeProduct = ref(defaultColor.id || product.id)
const swatchId = ref(defaultColor.id || product.id)

const showEyebrow = computed(() => {
  const { mode, routes } = $feature.eyebrowVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  return mode === 'show' ? match : !match
})

const { data } = useApi().products.details(activeProduct, {}, {
  immediate: false,
  key: `productCard-${activeProduct.value}`,
  default: () => ({
    ...product,
    url: defaultColor.url || productUrl.value,
    eyebrow: defaultColor.eyebrow || product.eyebrow,
    badge: defaultColor.badge || product.badge
  }),
  transform: (input) => ({
    ...input,
    eyebrow: formatBadgeOrEyebrow(
      input.eyebrow || product.eyebrow,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    ),
    badge: formatBadgeOrEyebrow(
      input.badge || product.badge,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
  })
}) as { data: Ref<Product>, pending: Ref<boolean> }

const categoryURL = breadcrumbs[breadcrumbs.length - 1]?.url
const colorsCount = 'availableColorsCount' in product ? product.availableColorsCount : 0
const url = computed(() => {
  if (data.value?.url && storeFilterState) {
    const baseUrl = `${data.value.url.startsWith('/') ? origin : ''}${data.value.url}`
    const parsedUrl = new URL(baseUrl)

    parsedUrl.searchParams.append('storeFilter', storeFilterState)

    return `${parsedUrl.pathname}${parsedUrl.search}`
  }

  return data.value?.url
})

const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop && getIsComingSoonProduct(data.value))
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && data.value?.isRobisOnly)

const hasBadge = computed(() => {
  const { mode, routes } = $feature.badgeVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  const isVisible = mode === 'show' ? match : !match
  return !!data.value.badge?.label && isVisible
})

const productPrice = computed(() => {
  if (!$feature.enablePlpSwatchDetails || data.value.displayPriceRange) return data.value.price

  const swatches = data.value?.attributes.find(({ type }) => type === 'color')?.options as ColorOption[]
  const colorSwatch = swatches?.find(({ id }) => id === swatchId.value) as ColorOption
  return colorSwatch?.price || data.value.price
})

const handleGtmEvent = (payload, action: ProductClickAction) =>
  $gtm.push('product.onProductClick', payload, {
    action,
    breadcrumbs,
    category: trackingCategory,
    offset: trackingOffset,
    searchTerm: route.query.q?.toString()
  })

const navigateToPDP = (e?: PointerEvent) => {
  handleGtmEvent(data.value, 'Navigate To PDP')

  const serializedBreadcrumbs = serializeAnalyticsBreadcrumbs(breadcrumbs)
  const { path, query = {} } = getRouteFromUrl(url.value)

  const state = {
    breadcrumbs: serializedBreadcrumbs,
    category: trackingCategory,
    searchTerm: route.query.q,
    categoryURL
  }

  if (e?.metaKey || e?.ctrlKey) {
    localStorage.setItem(`from_plp_${data.value.id}`, JSON.stringify(state))
  }
  else {
    e?.preventDefault()
    router.push({ path, query, state })
  }
}

const openQuickshop = () => {
  PanelQuickshop.open({
    product: data.value,
    categoryURL,
    gtmConfig: {
      ...(trackingCategory && { category: trackingCategory }),
      ...(storeFilterState && { shippingMethod: 'BOPIS_PLP_SD', shippingStoreId: storeFilterState, shippingWindow: '24Hrs' })
    }
  })
  handleGtmEvent(data.value, 'Open Quick Shop')
}
</script>
